import type { ReactElement } from 'react';
import { Row, Col, Empty } from 'antd';
import {
  StatWidget,
  StatSparklineWidget,
  LineChartWidget,
  BarChartWidget,
  PieChartWidget,
  DonutChartWidget,
} from './widgets';
import type { DashboardWidget, GridSpan, ReportConfig } from '../types/panelConfig';

interface DynamicDashboardProps {
  widgets: DashboardWidget[];
  reports: ReportConfig[];
}

const spanMap: Record<GridSpan, { xs: number; sm: number; lg: number }> = {
  1: { xs: 24, sm: 12, lg: 6 },
  2: { xs: 24, sm: 24, lg: 12 },
  3: { xs: 24, sm: 24, lg: 18 },
  4: { xs: 24, sm: 24, lg: 24 },
};

// Backward compat: older panel configs may still return 'stat-card' before the
// API deploys the widget_type rename migration. Normalize at read time.
function normalizeWidget(widget: DashboardWidget): DashboardWidget {
  const raw = widget as unknown as { type: string };
  if (raw.type === 'stat-card') {
    return { ...(widget as object), type: 'stat' } as DashboardWidget;
  }
  return widget;
}

function renderWidget(
  widget: DashboardWidget,
  reports: ReportConfig[],
): ReactElement | null {
  const report = reports.find((r) => r.code === widget.reportCode);
  const sourceId = report?.sourceId;
  if (!sourceId) return null;

  switch (widget.type) {
    case 'stat':
      return <StatWidget widget={widget} sourceId={sourceId} />;
    case 'stat-sparkline':
      return <StatSparklineWidget widget={widget} sourceId={sourceId} />;
    case 'chart-line':
      return <LineChartWidget widget={widget} sourceId={sourceId} />;
    case 'chart-bar':
      return <BarChartWidget widget={widget} sourceId={sourceId} />;
    case 'chart-pie':
      return <PieChartWidget widget={widget} sourceId={sourceId} />;
    case 'chart-donut':
      return <DonutChartWidget widget={widget} sourceId={sourceId} />;
  }
}

function defaultSpan(widget: DashboardWidget): GridSpan {
  if (widget.gridSpan) return widget.gridSpan;
  if (widget.type === 'stat' || widget.type === 'stat-sparkline') return 1;
  return 2;
}

export function DynamicDashboard({ widgets, reports }: DynamicDashboardProps) {
  const normalized = widgets.map(normalizeWidget);

  if (normalized.length === 0) {
    return <Empty description="Dashboard henüz yapılandırılmamış" />;
  }

  return (
    <div>
      <div style={{ margin: '4px 0 16px' }}>
        <h1
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            letterSpacing: '-0.01em',
          }}
        >
          Genel Bakış
        </h1>
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
          Son 30 gün · canlı demo verisi
        </div>
      </div>

      <Row gutter={[12, 12]}>
        {normalized.map((widget, idx) => {
          const span = spanMap[defaultSpan(widget)];
          return (
            <Col xs={span.xs} sm={span.sm} lg={span.lg} key={`${widget.type}-${widget.reportCode}-${idx}`}>
              {renderWidget(widget, reports)}
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
